import type { TPAPublicClientSpecEntry } from '@wix/thunderbolt-ssr-api'
import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { TpaWorkerSiteConfig, SingleTpaWorkerSiteConfig } from '@wix/thunderbolt-types'

const tpaWorkerSiteConfig: SiteConfigFn<TpaWorkerSiteConfig> = ({ api, funcLib }) => {
	const url = api.originalRequest.getUrl() ?? ''
	const tpasWorkerUrlOverrideParam = funcLib.getQueryParamValue(url, 'tpasWorkerUrlOverride')
	const workersOverrides = funcLib.parseCommaSeparatedObject(tpasWorkerUrlOverrideParam ?? undefined)

	const changeCSMKeysExperimentOn = !!api.env.getExperiments()['dm_changeCSMKeys']
	const deprecateAppIdExperimentOn = !!api.env.getExperiments()['specs.thunderbolt.deprecateAppId']

	const clientSpecMap = api.rendererModel.getClientSpecMap()

	// Remap CSM keys based on experiments
	let clientSpecMapByAppKey: Record<string, any>

	if (deprecateAppIdExperimentOn) {
		// Use appDefinitionId as key
		if (changeCSMKeysExperimentOn) {
			// CSM is already keyed by appDefinitionId, use as-is
			clientSpecMapByAppKey = clientSpecMap
		} else {
			// Remap to appDefinitionId
			clientSpecMapByAppKey = {}
			for (const [_key, csm] of Object.entries(clientSpecMap)) {
				const appDefId = (csm as TPAPublicClientSpecEntry).appDefinitionId
				if (appDefId) {
					clientSpecMapByAppKey[appDefId] = csm
				}
			}
		}
	} else {
		// Use applicationId as key (or original key if dm_changeCSMKeys is OFF)
		if (changeCSMKeysExperimentOn) {
			// Remap to applicationId
			clientSpecMapByAppKey = {}
			for (const [_key, csm] of Object.entries(clientSpecMap)) {
				const appId = csm.applicationId
				if (appId !== undefined) {
					clientSpecMapByAppKey[String(appId)] = csm
				}
			}
		} else {
			// Use original CSM keys
			clientSpecMapByAppKey = clientSpecMap
		}
	}

	// Filter and map workers
	const tpaWorkers: TpaWorkerSiteConfig['tpaWorkers'] = {}

	for (const [key, csm] of Object.entries(clientSpecMapByAppKey)) {
		const permissions = csm.permissions
		const appWorkerUrl = csm.appWorkerUrl

		// Filter: must have appWorkerUrl and permissions not revoked
		if (appWorkerUrl && (!permissions || !permissions.revoked)) {
			const appDefinitionId = csm.appDefinitionId
			const overrideUrl = appDefinitionId ? workersOverrides[appDefinitionId] : undefined
			const finalWorkerUrl = overrideUrl || appWorkerUrl

			const workerConfig: SingleTpaWorkerSiteConfig = {
				appWorkerUrl: finalWorkerUrl,
				appDefinitionName: csm.appDefinitionName || '',
			}

			// Add appDefinitionId only when deprecateAppId is OFF
			if (!deprecateAppIdExperimentOn && appDefinitionId) {
				workerConfig.appDefinitionId = appDefinitionId
			}

			tpaWorkers[key] = workerConfig
		}
	}

	const shouldLoadSiteFeature = Object.keys(tpaWorkers).length > 0

	return {
		shouldLoadSiteFeature,
		shouldLoadSiteConfig: shouldLoadSiteFeature,
		siteConfig: shouldLoadSiteFeature ? { tpaWorkers } : null,
	}
}

const name = 'tpaWorkerFeature'

export const config = { site: tpaWorkerSiteConfig, name }
